'use client';
import { createRef, useEffect, useRef, useState, Fragment } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useRouter, useSearchParams } from 'next/navigation';
import { findNearest } from 'geolib';

import LocationCard from './LocationCard';
import { normalizeObject } from 'src/services/content-utils';
import { cityUrl, locationUrl, stateUrl } from 'src/services/route-utils';
import SearchInput from 'src/components/SearchInput';
import useMapState from 'src/hooks/useMapState';

import { boundsForPoints, groupLocationsByCity, normalizeLocationsByCity } from '../../locatii/utils';
import MobileMapChevron from './MobileMapChevron';
import Breadcrumb from 'src/components/Breadcrumb';

type Props = {
    serverSideData: {
        locations: any[];
        parts: string[];
        states: any[];
        selectedStateSlug?: string | null;
        selectedCitySlug?: string | null;
        mapboxResult?: { name: string; geo_point: [number, number] };
    };
};

export default function LocationsView({ serverSideData }: Props) {
    const router = useRouter();
    const searchFormRef = createRef<HTMLFormElement>();
    const disableRenderedLocationsChangeRef = useRef(false);
    const notFoundTextRef = useRef<string | null>(null);
    const [notFound, setNotFound] = useState<string | null>(null);
    const { locationsMap: map } = useMap();
    const {
        allLocations,
        initialized,
        stateCitiesMap,
        renderedLocations: _renderedLocations,
        setRenderedLocations: _setRenderedLocations,
        setHoveredItem,
        setInitialized,
    } = useMapState();
    const [renderedLocations, setRenderedLocations] = useState(groupLocationsByCity(serverSideData.locations));

    const [selectedState, setSelectedState] = useState(
        serverSideData.states.find((i) => i.slug === serverSideData.selectedStateSlug) || null,
    );
    const [cities, setCities] = useState<any[]>(
        selectedState?.slug ? stateCitiesMap?.[selectedState?.slug] || null : null,
    );
    const [selectedCity, setSelectedCity] = useState(
        (cities?.length !== 1 ? cities?.find((i) => i.slug === serverSideData.selectedCitySlug) : cities?.[0]) || null,
    );

    const boundsForLocations = (locations) => {
        return boundsForPoints(locations.map((i) => normalizeObject(i.geo_point)));
    };

    const searchParams = useSearchParams();

    useEffect(() => {
        changeRenderedLocations(serverSideData.locations);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        if (!stateCitiesMap) return;

        const cities = selectedState?.slug ? stateCitiesMap?.[selectedState?.slug] || [] : [];

        let selectedCity: any = null;

        if (cities.length === 1) {
            selectedCity = cities[0];
        } else if (cities.length && renderedLocations?.length === 1) {
            selectedCity = cities?.find((i) => i.slug === renderedLocations[0].city?.slug) || null;
        }

        setCities(cities);
        setSelectedCity(selectedCity);
    }, [stateCitiesMap, selectedState, serverSideData, renderedLocations]);

    useEffect(() => {
        if (!stateCitiesMap) return;

        if (!initialized) {
            setInitialized(true);
        }

        if (serverSideData.mapboxResult) {
            const flatLocations = Object.keys(allLocations || {}).reduce(
                (current, key) => [...current, ...allLocations?.[key].locations],
                [],
            );
            const nearestGeo = findNearest(
                serverSideData.mapboxResult.geo_point,
                flatLocations.map((l) => JSON.parse(l.geo_point)),
            );
            const nearest = flatLocations.find((l) => l.geo_point === JSON.stringify(nearestGeo));

            if (nearest) {
                const locations = allLocations?.find((l) => l.city.id === nearest.city.id)?.locations;
                const bounds = boundsForPoints(
                    [
                        serverSideData.mapboxResult.geo_point,
                        ...(locations || []).map((i) => normalizeObject(i.geo_point)),
                    ],
                    0.1,
                );

                notFoundTextRef.current = serverSideData.mapboxResult.name;
                map?.fitBounds(bounds, { animate: true, duration: 2000 });
            }
        } else {
            disableRenderedLocationsChangeRef.current = true;
            _setRenderedLocations(serverSideData.locations);
            navigateMapToLocations(serverSideData.locations, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateCitiesMap, searchParams]);

    useEffect(() => {
        if (_renderedLocations && _renderedLocations !== serverSideData.locations) {
            setNotFound(null);
            if (disableRenderedLocationsChangeRef.current) {
                disableRenderedLocationsChangeRef.current = false;
            } else {
                changeRenderedLocations(_renderedLocations);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_renderedLocations]);

    const changeRenderedLocations = (locations) => {
        const grouped = groupLocationsByCity(locations);
        setRenderedLocations(grouped);

        let stateSlug: string | null = null;
        let citySlug: string | null = null;
        if (grouped.length === 1) {
            stateSlug = grouped[0].city.state.slug;
            citySlug = grouped[0].city.slug;
        } else {
            const states = grouped.reduce((current: string[], i) => {
                if (current.find((s) => s === i.city.state.slug)) return current;

                return [...current, i.city.state.slug];
            }, []);
            if (states.length === 1) {
                stateSlug = states[0];
            }
        }

        changeStateAndCity(stateSlug, citySlug, false);

        window.scrollTo({ top: 0 });

        if (notFoundTextRef.current) {
            setNotFound(notFoundTextRef.current);
            notFoundTextRef.current = null;
        }
    };

    const navigateMapToLocations = (locations, duration = 2000) => {
        if (locations.length) {
            map?.fitBounds(boundsForLocations(locations), { animate: !!duration, duration });
        }
    };

    const changeStateAndCity = (stateSlug, citySlug, reloadLocations = true, resetSearchForm = true) => {
        setSelectedState(stateSlug ? serverSideData.states.find((s) => s.slug === stateSlug) : null);
        const cities = stateSlug ? stateCitiesMap?.[stateSlug] || null : null;
        setCities(cities);
        setSelectedCity(
            stateSlug && citySlug ? cities?.find((c) => c.slug === citySlug) : cities?.length === 1 ? cities[0] : null,
        );

        const route = citySlug
            ? cityUrl({ slug: citySlug, state: { slug: stateSlug } })
            : stateUrl(stateSlug ? { slug: stateSlug } : null);

        if (reloadLocations) {
            router.replace(route);
        }

        if (resetSearchForm) {
            searchFormRef.current?.reset();
        }

        if (reloadLocations) {
            const locations = normalizeLocationsByCity(
                (allLocations || [])?.filter((i) => {
                    if (citySlug) return i.city.slug === citySlug;
                    if (stateSlug) return i.city.state.slug === stateSlug;
                    return true;
                }) || [],
            );
            disableRenderedLocationsChangeRef.current = true;
            changeRenderedLocations(locations);
            navigateMapToLocations(locations);
        }
    };

    const breadcrumbItems = [
        { url: stateUrl(null), label: 'Locații' },
        ...(selectedState ? [{ url: stateUrl(selectedState), label: selectedState.name || '' }] : []),
        ...(selectedCity ? [{ url: cityUrl(selectedCity), label: selectedCity.name || '' }] : []),
    ];
    let heading = 'Centre de analize Bioclinica';
    if (selectedCity) {
        heading = `${heading} în ${selectedCity.name}`;
    } else if (selectedState) {
        heading = `${heading} în județul ${selectedState.name}`;
    }

    return (
        <>
            <MobileMapChevron />
            <Breadcrumb className="p-4 md:px-8" items={breadcrumbItems} />
            <h1 className="font-body text-primary px-4 text-center font-bold md:px-8">{heading}</h1>
            <div className="top-nav-sm-height md:top-nav-md-height bg-lightest-grey shadow-lightest-grey sticky p-4 shadow-lg transition-all md:px-8 md:shadow-xl">
                <SearchInput
                    id="locations-search"
                    ref={searchFormRef}
                    disabled={!allLocations}
                    placeholder="Caută după numele localității"
                    ariaLabel="Introduceți numele localității"
                    redirectUrl={locationUrl(null)}
                    onSubmit={() => setNotFound(null)}
                    filterParam="q"
                />
                <div className="mt-4 flex flex-row gap-4">
                    <select
                        className="flex-1"
                        disabled={!allLocations}
                        value={selectedState?.slug || ''}
                        onChange={(e) => changeStateAndCity(e.target.value || null, null)}>
                        <option value="">Județ</option>
                        {serverSideData.states?.map((s) => (
                            <option key={s.slug} value={s.slug}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="flex-1"
                        disabled={!allLocations || !selectedState || cities?.length === 1}
                        value={selectedCity?.slug || ''}
                        onChange={(e) => changeStateAndCity(selectedState.slug, e.target.value || null)}>
                        <option value="">Localitate</option>
                        {cities?.map((c) => (
                            <option key={c.slug} value={c.slug}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="px-4 md:px-8">
                {notFound && (
                    <>
                        <div>Nu am găsit niciun centru Bioclinica în &apos;{notFound}&apos;.</div>
                        <div>Vezi mai jos centrele cele mai apropiate de locația căutată de tine.</div>
                    </>
                )}
                {renderedLocations.map((item) => {
                    const locationsCount = allLocations?.find((i) => i.city.slug === item.city.slug)?.locations.length;
                    const locationsNotVisible = (locationsCount || item.locations.length) - item.locations.length;

                    return (
                        <Fragment key={item.city.slug}>
                            <div className="my-4 flex flex-row items-end md:my-8">
                                <h2 className="flex-1 text-3xl lg:text-4xl">{item.city.name}</h2>
                                <div className="w-1/4 text-right text-xs md:text-sm">
                                    {locationsCount &&
                                        `${locationsCount} ${locationsCount === 1 ? 'locație' : 'locații'}`}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:gap-8">
                                {item.locations.map((l) => (
                                    <LocationCard
                                        headingType="h3"
                                        onMouseEnter={() => setHoveredItem(l)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                        key={l.slug}
                                        location={l}
                                    />
                                ))}
                                {!!locationsNotVisible && (
                                    <div className="items-center text-center text-xl">
                                        <div
                                            className="text-primary cursor-pointer underline"
                                            onClick={() => changeStateAndCity(selectedState.slug, item.city.slug)}>
                                            +
                                            {` ${
                                                locationsNotVisible === 1
                                                    ? 'încă o locație'
                                                    : `alte ${locationsNotVisible} locații`
                                            } `}
                                            în afara hărții afișate
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Fragment>
                    );
                })}
            </div>
        </>
    );
}
